import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import menuIcon from "../../assets/images/menuIcon.svg";
import notificationIcon from "../../assets/images/notification.svg";
import searchIcon from "../../assets/images/search.svg";
import closeIcon from "../../assets/images/closeIcon.svg";
import forwardArrow from "../../assets/images/forwardArrow.svg";
import userAddIcon from "../../assets/images/userAddIcon.svg";
import logo from "../../assets/images/mmt/logo.png";
import {
  fetchLoggedUserInfo,
  fetchTimeline,
  fetchUserList,
  logoutMmt,
} from "./mmtApi";
import {
  appendQueryParamsToUrl,
  appendTabToUrl,
  buildActivityMonitoringUrl,
  buildDashboardUrl,
  clearStoredSession,
  formatPersonName,
  getStoredSession,
  normalizeReportUrl,
  openAddTestPerson,
  openEditTestPerson,
  quickJumpToHash,
} from "./mmtFlow";
import {
  getAboutUsUrl,
  getContactUsUrl,
  getPrivacyPolicyUrl,
  getTermsUrl,
} from "../../configs/environment";
import NotificationTimelineDrawer from "./NotificationTimelineDrawer";
import MmtAddTestPerson from "../testPerson/MmtAddTestPerson";
import MmtEditTestPerson from "../testPerson/MmtEditTestPerson";
import "./mmtuserlist.scss";

const PAGE_LIMIT = 15;

export default function MmtUserList() {
  const navigate = useNavigate();
  const [session] = useState(getStoredSession);
  const [userInfo, setUserInfo] = useState(null);
  const [users, setUsers] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [search, setSearch] = useState("");
  const [sortOption, setSortOption] = useState("createdAt_desc");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [error, setError] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [logoutConfirm, setLogoutConfirm] = useState(false);
  // modal: null | { type: 'add' } | { type: 'edit', seniorUuid } | { type: 'view', seniorUuid }
  const [modal, setModal] = useState(null);

  useEffect(() => {
    if (!session) navigate("/login", { replace: true });
  }, [navigate, session]);

  const [sortField, sortOrder] = useMemo(
    () => sortOption.split("_"),
    [sortOption],
  );

  const loadUsers = useCallback(
    async (nextPage = 1, append = false) => {
      if (!session) return;
      append ? setLoadingMore(true) : setLoading(true);
      setError("");
      try {
        const data = await fetchUserList({
          session,
          page: nextPage,
          limit: PAGE_LIMIT,
          filter: search.trim(),
          sortField,
          sortOrder,
        });
        setUsers((current) => (append ? [...current, ...data] : data));
        setHasMore(data.length === PAGE_LIMIT);
        setPage(nextPage);
      } catch (err) {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Unable to load test persons.",
        );
      } finally {
        append ? setLoadingMore(false) : setLoading(false);
      }
    },
    [search, session, sortField, sortOrder],
  );

  useEffect(() => {
    if (!session) return;
    fetchLoggedUserInfo(session)
      .then(setUserInfo)
      .catch(() => {});
  }, [session]);

  useEffect(() => {
    const id = window.setTimeout(() => loadUsers(1, false), 350);
    return () => window.clearTimeout(id);
  }, [loadUsers]);

  const openTimeline = async () => {
    if (!session) return;
    setTimelineOpen(true);
    setTimelineLoading(true);
    try {
      setTimeline(await fetchTimeline(session));
    } catch {
      setTimeline([]);
    } finally {
      setTimelineLoading(false);
    }
  };

  const goTo = (url) => {
    if (url.startsWith("/")) navigate(url);
    else window.location.href = url;
  };

  const handleNotificationPress = (notification) => {
    setTimelineOpen(false);
    if (
      notification.category === "SENSOR_DATA" &&
      (notification.source || notification.sourceTool || "").toUpperCase() ===
        "WELLTRA"
    ) {
      goTo(
        appendTabToUrl(
          buildDashboardUrl(session, notification.quickJump?.seniorId || ""),
          "monitor",
        ),
      );
      return;
    }
    if (
      (notification.source || notification.sourceTool || "").toUpperCase() ===
      "PONTOSENSE"
    ) {
      goTo(
        buildActivityMonitoringUrl(
          session,
          notification.quickJump?.seniorId || "",
        ),
      );
      return;
    }
    if (
      (notification.quickJump?.action || "").toUpperCase() ===
        "SEE_RISK_INSIGHTS" &&
      notification.quickJump?.reportId
    ) {
      goTo(
        appendQueryParamsToUrl(
          appendTabToUrl(
            buildDashboardUrl(session, notification.quickJump?.seniorId || ""),
            "risk",
          ),
          { reportId: notification.quickJump?.reportId },
        ),
      );
      return;
    }
    if (notification.openUrl) {
      goTo(
        appendQueryParamsToUrl(
          normalizeReportUrl(notification.openUrl, session),
          {
            moduleId: notification.quickJump?.moduleId,
          },
        ),
      );
      return;
    }
    const hash = notification.quickJump?.destination
      ? quickJumpToHash(notification.quickJump.destination)
      : undefined;
    goTo(
      appendQueryParamsToUrl(
        buildDashboardUrl(
          session,
          notification.quickJump?.seniorId || "",
          hash,
        ),
        {
          moduleId: notification.quickJump?.moduleId,
        },
      ),
    );
  };

  const handleLogout = async () => {
    if (session) await logoutMmt(session);
    clearStoredSession();
    navigate("/login", { replace: true });
  };

  if (!session) return null;

  return (
    <main className="mmt-user-list-page">
      <header className="mmt-user-header">
        <button
          className="mmt-header-icon"
          onClick={() => setMenuOpen(true)}
          aria-label="Open menu"
        >
          <img src={menuIcon} alt="" />
        </button>
        <div className="mmt-user-welcome">
          {userInfo?.picture ? <img src={userInfo.picture} alt="" /> : <span />}
          <strong>Welcome {userInfo?.name || ""}</strong>
        </div>
        <button
          className="mmt-header-icon"
          onClick={openTimeline}
          aria-label="Open notifications"
        >
          <img src={notificationIcon} alt="" />
        </button>
      </header>

      <section className="mmt-list-toolbar">
        <h1>Test Persons</h1>
        <div className="mmt-search-sort">
          <label className="mmt-search">
            <img src={searchIcon} alt="" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="First name, last name, ID"
            />
            {search ? (
              <button onClick={() => setSearch("")} aria-label="Clear search">
                <img src={closeIcon} alt="" />
              </button>
            ) : null}
          </label>
          <select
            value={sortOption}
            onChange={(event) => setSortOption(event.target.value)}
          >
            <option value="createdAt_desc">Date Created: Newest</option>
            <option value="createdAt_asc">Date Created: Oldest</option>
          </select>
        </div>
      </section>

      <section className="mmt-users">
        {loading ? (
          <div className="mmt-state">Loading...</div>
        ) : error ? (
          <div className="mmt-state">
            <p>{error}</p>
            <button onClick={() => loadUsers(1, false)}>Retry</button>
          </div>
        ) : users.length ? (
          <>
            {users.map((senior) => {
              const name = formatPersonName(
                senior.first_name,
                senior.last_name,
                session.locale,
              );
              return (
                <button
                  className="mmt-user-card"
                  key={senior.id}
                  onClick={() => goTo(buildDashboardUrl(session, senior.id))}
                >
                  <img
                    className="mmt-user-avatar"
                    src={senior.picture || logo}
                    alt=""
                  />
                  <span className="mmt-user-card-copy">
                    <strong>{name || senior.name}</strong>
                    <span className="mmt-badges">
                      <span>ID: {senior.test_person_id}</span>
                      {senior.gender || senior.age > 0 ? (
                        <span>
                          {senior.gender}
                          {senior.gender && senior.age > 0 ? <i /> : null}
                          {senior.age > 0 ? senior.age : ""}
                        </span>
                      ) : null}
                    </span>
                    {senior.isIncompleteProfile ? (
                      <em
                        onClick={(event) => {
                          event.stopPropagation();
                          openEditTestPerson(session, senior.id, setModal);
                        }}
                      >
                        Complete the Profile
                      </em>
                    ) : null}
                  </span>
                  <img className="mmt-forward" src={forwardArrow} alt="" />
                </button>
              );
            })}
            {hasMore ? (
              <button
                className="mmt-load-more"
                onClick={() => loadUsers(page + 1, true)}
                disabled={loadingMore}
              >
                {loadingMore ? "Loading..." : "Load More"}
              </button>
            ) : null}
          </>
        ) : (
          <div className="mmt-state">
            {search ? "No matching test persons" : "No test persons found"}
          </div>
        )}
      </section>

      <button
        className="mmt-add-person"
        onClick={() => openAddTestPerson(session, setModal)}
      >
        <img src={userAddIcon} alt="" />
        Add Test Person
      </button>

      <div
        className={`mmt-drawer-overlay ${menuOpen ? "is-open" : ""}`}
        aria-hidden={!menuOpen}
      >
        <button
          className="mmt-drawer-backdrop"
          onClick={() => setMenuOpen(false)}
          aria-label="Close menu"
        />
        <aside className="mmt-menu-drawer">
          <button
            className="mmt-menu-close"
            onClick={() => setMenuOpen(false)}
            aria-label="Close menu"
          >
            <img src={closeIcon} alt="" />
          </button>

          <div className="mmt-menu-profile">
            {userInfo?.picture ? (
              <img src={userInfo.picture} alt="" className="mmt-menu-avatar" />
            ) : (
              <div className="mmt-menu-avatar mmt-menu-avatar-placeholder">
                <span>{userInfo?.name?.charAt(0)?.toUpperCase() || "?"}</span>
              </div>
            )}
            <div className="mmt-menu-profile-info">
              <strong>{userInfo?.name || "Senior Life"}</strong>
              {userInfo?.email ? <span>{userInfo.email}</span> : null}
            </div>
          </div>

          <div className="mmt-menu-divider" />

          <nav className="mmt-menu-items">
            <button
              className="mmt-menu-item"
              onClick={() => {
                setMenuOpen(false);
                window.open(
                  getPrivacyPolicyUrl(session?.locale),
                  "_blank",
                  "noreferrer",
                );
              }}
            >
              <span className="mmt-menu-item-icon">
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#054997"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </span>
              <span className="mmt-menu-item-text">
                <strong>Privacy &amp; Data Policy</strong>
                <span>Data usage &amp; Payment services</span>
              </span>
              <svg
                className="mmt-menu-chevron"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#94a3b8"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>

            <button
              className="mmt-menu-item"
              onClick={() => {
                setMenuOpen(false);
                window.open(
                  getTermsUrl(session?.locale),
                  "_blank",
                  "noreferrer",
                );
              }}
            >
              <span className="mmt-menu-item-icon">
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#054997"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                  <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
                </svg>
              </span>
              <span className="mmt-menu-item-text">
                <strong>Terms of Service</strong>
                <span>Check out our terms for usage guidelines.</span>
              </span>
              <svg
                className="mmt-menu-chevron"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#94a3b8"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>

            <button
              className="mmt-menu-item"
              onClick={() => {
                setMenuOpen(false);
                window.open(
                  getAboutUsUrl(session?.locale),
                  "_blank",
                  "noreferrer",
                );
              }}
            >
              <span className="mmt-menu-item-icon">
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#054997"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 16v-4M12 8h.01" />
                </svg>
              </span>
              <span className="mmt-menu-item-text">
                <strong>About Us</strong>
                <span>Discover more about our story and mission</span>
              </span>
              <svg
                className="mmt-menu-chevron"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#94a3b8"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>

            <button
              className="mmt-menu-item"
              onClick={() => {
                setMenuOpen(false);
                window.open(
                  getContactUsUrl(session?.locale),
                  "_blank",
                  "noreferrer",
                );
              }}
            >
              <span className="mmt-menu-item-icon">
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#054997"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
                </svg>
              </span>
              <span className="mmt-menu-item-text">
                <strong>Contact Support</strong>
                <span>Reach out for assistance with our support team</span>
              </span>
              <svg
                className="mmt-menu-chevron"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#94a3b8"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>

            <button
              className="mmt-menu-item mmt-menu-item-logout"
              onClick={() => {
                setMenuOpen(false);
                setLogoutConfirm(true);
              }}
            >
              <span className="mmt-menu-item-icon">
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#e53e3e"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
                </svg>
              </span>
              <span className="mmt-menu-item-text">
                <strong>Logout</strong>
                <span>After logout you will not receive notifications</span>
              </span>
              <svg
                className="mmt-menu-chevron"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#94a3b8"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          </nav>
        </aside>
      </div>

      {logoutConfirm ? (
        <div className="mmt-confirm-overlay">
          <div className="mmt-confirm-dialog">
            <strong>Logout</strong>
            <p>Are you sure you want to log out?</p>
            <div className="mmt-confirm-actions">
              <button onClick={() => setLogoutConfirm(false)}>Cancel</button>
              <button
                className="mmt-confirm-destructive"
                onClick={() => {
                  setLogoutConfirm(false);
                  handleLogout();
                }}
              >
                Log Out
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <NotificationTimelineDrawer
        open={timelineOpen}
        timeline={timeline}
        loading={timelineLoading}
        onClose={() => setTimelineOpen(false)}
        onNotificationPress={handleNotificationPress}
      />

      {modal ? (
        <div className="mmt-tp-overlay">
          {modal.type === "add" ? (
            <MmtAddTestPerson
              onClose={() => setModal(null)}
              onSuccess={() => {
                setModal(null);
                loadUsers(1, false);
              }}
            />
          ) : modal.type === "edit" ? (
            <MmtEditTestPerson
              seniorUuidProp={modal.seniorUuid}
              onClose={() => setModal(null)}
              onSuccess={() => {
                setModal(null);
                loadUsers(1, false);
              }}
            />
          ) : modal.type === "view" ? (
            <MmtEditTestPerson
              readOnly
              seniorUuidProp={modal.seniorUuid}
              onClose={() => setModal(null)}
            />
          ) : null}
        </div>
      ) : null}
    </main>
  );
}
